/**
 * Filtro de opções dentro do <select> nativo — aplicado globalmente.
 *
 * O app usa o "customizable select" do Chrome (appearance: base-select no
 * styles.scss), que permite conteúdo HTML dentro do picker do select. CSS não
 * cria elementos, então este módulo injeta um <input class="select-filter">
 * como primeiro filho de cada .form-select — o estilo do input vive no
 * styles.scss (seção "Select customizável"), aplicado globalmente.
 *
 * Comportamento: ao abrir, o Chrome foca o input (primeiro focável do picker);
 * digitar esconde as opções que não contêm o texto (sem acentos e sem
 * maiúsculas); Enter aplica a primeira opção visível e fecha; ao fechar/reabrir
 * o filtro é limpo. Sem suporte a base-select (outros browsers), nada é feito e
 * o select continua 100% nativo. Opt-out por elemento: <select data-no-filter>.
 */

const normalize = (text: string): string =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function installSelectFilter(doc: Document = document): void {
  if (!CSS.supports('appearance', 'base-select')) return;

  // Delegação no documento: cobre selects renderizados a qualquer momento pelo Angular.
  doc.addEventListener(
    'pointerdown',
    (event) => {
      const select = (event.target as Element | null)?.closest?.('select.form-select');
      if (select instanceof HTMLSelectElement) enhance(select);
    },
    true,
  );
  // Abertura por teclado (Enter/Espaço/Alt+Seta com o select focado).
  doc.addEventListener(
    'keydown',
    (event) => {
      const select = event.target;
      if (select instanceof HTMLSelectElement && select.matches('select.form-select')) enhance(select);
    },
    true,
  );
}

function enhance(select: HTMLSelectElement): void {
  if (select.multiple || select.dataset['noFilter'] !== undefined) return;
  const existing = select.querySelector<HTMLInputElement>('input.select-filter');
  if (existing) {
    reset(select, existing); // reabertura: começa sem filtro
    return;
  }

  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = 'Filtrar…';
  input.autocomplete = 'off';
  input.className = 'select-filter';
  select.prepend(input);

  input.addEventListener('input', () => {
    const term = normalize(input.value.trim());
    for (const option of Array.from(select.options)) {
      option.hidden = term !== '' && !normalize(option.textContent ?? '').includes(term);
    }
  });

  input.addEventListener('keydown', (event) => {
    // event.key pode vir vazio em teclados sintéticos (automação/IME) — usa o code como fallback.
    if (event.key === 'Enter' || event.code === 'Enter' || event.code === 'NumpadEnter') {
      event.preventDefault();
      applyFirstVisible(select, input);
      return;
    }
    // Impede o typeahead/navegação do select de reagir às teclas digitadas no input.
    if (event.key !== 'Escape') event.stopPropagation();
  });

  // Opção escolhida com clique → limpa o filtro para a próxima abertura.
  select.addEventListener('change', () => reset(select, input));
}

/** Enter: seleciona a primeira opção visível, avisa o Angular e fecha o picker. */
function applyFirstVisible(select: HTMLSelectElement, input: HTMLInputElement): void {
  const first = Array.from(select.options).find((option) => !option.hidden && !option.disabled);
  if (!first) return;
  select.value = first.value;
  select.dispatchEvent(new Event('input', { bubbles: true }));
  select.dispatchEvent(new Event('change', { bubbles: true })); // já limpa o filtro (listener acima)
  closePicker(select);
}

/**
 * Não há API para fechar o picker programaticamente; alternar o appearance
 * para o select nativo por um frame destrói o popover (imperceptível).
 */
function closePicker(select: HTMLSelectElement): void {
  select.style.appearance = 'auto';
  requestAnimationFrame(() => {
    select.style.appearance = '';
    select.focus();
  });
}

function reset(select: HTMLSelectElement, input: HTMLInputElement): void {
  input.value = '';
  for (const option of Array.from(select.options)) option.hidden = false;
}
